// Shared by the chat's confirm card, artifact cards and section dividers: their copy (the
// "chat" part of public/content/agent-approvals.json) and their tokens (design.md §5.1).
import { useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { useContent } from '../../utils/content';

/** The chat cards' copy, or null until the file has loaded. */
export const useChatCardCopy = () => useContent('agent-approvals')?.chat || null;

/** The text with its {placeholders} filled in. */
export const fill = (text, values = {}) =>
  String(text ?? '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));

export const useChatCardTokens = () => {
  const theme = useTheme();
  return useMemo(() => {
    const { slate, blue } = theme.palette.modern;
    const primary = theme.palette.primary.main;
    const motion = theme.modern.motion.standard;
    return {
      slate,
      primary,
      line: slate[200],
      lineHover: blue[300],
      soft: alpha(primary, 0.08),
      surface: theme.palette.background.paper,
      motion,
      emphasis: theme.modern.motion.emphasis,
      tone: (role) => {
        const palette = theme.palette[role] || theme.palette.info;
        return { fg: palette.dark || palette.main, bg: alpha(palette.main, 0.12) };
      },
      /** A card that enters with a short rise, or a fade only for reduced motion (design.md §5.6). */
      enter: {
        '@keyframes chatCardIn': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'none' },
        },
        animation: `chatCardIn 200ms ease-out`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      },
      focusRing: { outline: `2px solid ${primary}`, outlineOffset: 2 },
    };
  }, [theme]);
};
